import { useMemo } from 'react';
import { useCurrentUser } from './currentUserContext';

/**
 * The permissions that a user can have in the app.
 * These are used to control access to various features and actions.
 *
 * **Note:** Not all permissions are listed here - only those that are used in the app. Check the backend for the full list of permissions.
 */
export type UserPermissions =
  | 'ADMIN_ACCESS'
  | 'CHAT_ACCESS'
  | 'PEOPLE_ACCESS'
  | 'NUDGES_ACCESS'
  | 'POSTS_CREATE_IMPORTANT_POSTS'
  | 'CHAT_MESSAGE_EDIT_MY_MESSAGES'
  | 'CHAT_MESSAGE_DELETE_MY_MESSAGES'
  | 'CHAT_CHANNEL_CREATE_MULTI_USERS'
  | 'PERFORM_IQ_KPIS_ACCESS'
  | 'JOURNEYS_ACCESS'
  | 'PROCEDURES_ACCESS'
  | 'POLICIES_ACCESS'
  | 'ANALYTICS_DASHBOARD_ACCESS'
  | 'CHECKLIST_ADMIN'
  | 'UNIT_EMPLOYEES_EDIT'
  | 'OPTIONAL_USER_ONBOARDING_FIELDS_ACCESS';

export type HasPermission =
  | UserPermissions
  | { someOf: UserPermissions[]; allOf?: never }
  | { allOf: UserPermissions[]; someOf?: never };

export const useHasPermission = (checkFor: HasPermission) => {
  const user = useCurrentUser();
  const permissions = useMemo(() => new Set(user.permissions), [user]);

  return useMemo(() => {
    if (typeof checkFor === 'string') {
      return permissions.has(checkFor);
    } else if (checkFor.someOf) {
      return !permissions.isDisjointFrom(new Set(checkFor.someOf));
    } else {
      return new Set(checkFor.allOf).isSubsetOf(permissions);
    }
  }, [permissions, checkFor]);
};
